import FormPage from '../../components/ui/FormPage.tsx';
import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery } from 'react-query';
import { ArrowLeftIcon, PlusIcon } from '@heroicons/react/24/outline';
import api from '../../services/api.ts';
import { handlePtBrSubmit, ptBrValidityProps } from '../../utils/htmlValidation.ts';
import { assertNonNegative } from '../../utils/numberGuards.ts';

const FrotaNovaManutencao: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    type: 'preventive',
    description: '',
    workshop: '',
    kmAtService: '',
    amount: '',
    serviceDate: new Date().toISOString().split('T')[0],
    nextPreventiveKm: '',
    postToCashFlow: false,
    notes: '',
  });

  const { data: vehicle } = useQuery(
    ['fleet-vehicle', id],
    async () => (await api.get(`/patrimonio/frota/${id}`)).data,
    { enabled: !!id }
  );

  const mutation = useMutation(
    async () => {
      const payload: any = {
        type: form.type,
        description: form.description,
        workshop: form.workshop || null,
        kmAtService: form.kmAtService === '' ? vehicle?.currentKm : Number(form.kmAtService),
        amount: Number(form.amount) || 0,
        serviceDate: form.serviceDate,
        nextPreventiveKm:
          form.nextPreventiveKm === '' ? undefined : Number(form.nextPreventiveKm),
        postToCashFlow: form.postToCashFlow,
        notes: form.notes || undefined,
      };
      return (await api.post(`/patrimonio/frota/${id}/manutencoes`, payload)).data;
    },
    {
      onSuccess: () => navigate(`/patrimonio/frota/${id}/manutencoes`),
      onError: (err: any) =>
        setError(err?.response?.data?.error || 'Erro ao registrar manutenção'),
    }
  );

  const set = (k: string, v: any) => setForm(prev => ({ ...prev, [k]: v }));

  return (
    <FormPage
      backTo={`/patrimonio/frota/${id}/manutencoes`}
      backLabel="Voltar"
      breadcrumb="Patrimônio / Frota / Nova manutenção"
      title="Nova manutenção"
      subtitle={`${vehicle?.make || ''} ${vehicle?.model || ''} · ${vehicle?.licensePlate || ''}`.trim()}
    >
{error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm">
          {error}
        </div>
      )}

      <form
        className="space-y-4"
        noValidate
        onSubmit={e =>
          handlePtBrSubmit(e, () => {
            const errs = [
              assertNonNegative(form.kmAtService, 'Km no serviço'),
              assertNonNegative(form.amount, 'Valor'),
              assertNonNegative(form.nextPreventiveKm, 'Próx. preventiva (km)'),
            ].filter(Boolean);
            if (errs.length) {
              setError(errs[0] as string);
              return;
            }
            setError('');
            mutation.mutate();
          })
        }
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <label className="text-sm">
            <span className="text-gray-600 block mb-1">Tipo *</span>
            <select
              value={form.type}
              onChange={e => set('type', e.target.value)}
              className="w-full border border-gray-300 rounded-xl px-3 py-2"
            >
              <option value="preventive">Preventiva</option>
              <option value="corrective">Corretiva</option>
            </select>
          </label>
          <label className="text-sm">
            <span className="text-gray-600 block mb-1">Data *</span>
            <input
              required
              {...ptBrValidityProps}
              type="date"
              value={form.serviceDate}
              onChange={e => set('serviceDate', e.target.value)}
              className="w-full border border-gray-300 rounded-xl px-3 py-2"
            />
          </label>
          <label className="text-sm sm:col-span-2">
            <span className="text-gray-600 block mb-1">Descrição *</span>
            <input
              required
              {...ptBrValidityProps}
              value={form.description}
              onChange={e => set('description', e.target.value)}
              className="w-full border border-gray-300 rounded-xl px-3 py-2"
              placeholder="Revisão 40 mil · óleo, filtros…"
            />
          </label>
          <label className="text-sm">
            <span className="text-gray-600 block mb-1">Oficina</span>
            <input
              value={form.workshop}
              onChange={e => set('workshop', e.target.value)}
              className="w-full border border-gray-300 rounded-xl px-3 py-2"
            />
          </label>
          <label className="text-sm">
            <span className="text-gray-600 block mb-1">Km no serviço</span>
            <input
              type="number"
              inputMode="numeric"
              {...ptBrValidityProps}
              value={form.kmAtService}
              onChange={e => set('kmAtService', e.target.value)}
              placeholder={String(vehicle?.currentKm || '')}
              className="w-full border border-gray-300 rounded-xl px-3 py-2 font-mono"
            />
          </label>
          <label className="text-sm">
            <span className="text-gray-600 block mb-1">Valor (R$)</span>
            <input
              type="number"
                            step="0.01"
              value={form.amount}
              onChange={e => set('amount', e.target.value)}
              className="w-full border border-gray-300 rounded-xl px-3 py-2"
            />
          </label>
          <label className="text-sm">
            <span className="text-gray-600 block mb-1">Próx. preventiva (km)</span>
            <input
              type="number"
              inputMode="numeric"
              {...ptBrValidityProps}
              value={form.nextPreventiveKm}
              onChange={e => set('nextPreventiveKm', e.target.value)}
              className="w-full border border-gray-300 rounded-xl px-3 py-2 font-mono"
            />
          </label>
          <label className="text-sm sm:col-span-2">
            <span className="text-gray-600 block mb-1">Observações</span>
            <textarea
              value={form.notes}
              onChange={e => set('notes', e.target.value)}
              rows={3}
              className="w-full border border-gray-300 rounded-xl px-3 py-2"
            />
          </label>
        </div>

        <label className="flex items-start gap-3 text-sm bg-slate-50 border border-slate-200 rounded-xl px-4 py-3">
          <input
            type="checkbox"
            checked={form.postToCashFlow}
            onChange={e => set('postToCashFlow', e.target.checked)}
            className="mt-0.5"
          />
          <span>
            <strong className="block text-gray-900">Lançar no Fluxo de caixa</strong>
            <span className="text-gray-500">
              Cria uma saída no financeiro (opcional — não é CTA primário).
            </span>
          </span>
        </label>

        <div className="flex justify-end gap-3 pt-2">
          <button
            type="button"
            onClick={() => navigate(`/patrimonio/frota/${id}/manutencoes`)}
            className="btn btn-secondary"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={mutation.isLoading}
            className="btn btn-primary"
          >
            {mutation.isLoading ? 'Salvando…' : 'Salvar manutenção'}
          </button>
        </div>
      </form>
    </FormPage>
  );
};

export default FrotaNovaManutencao;
